/**
 * Regroup a streamed assistant message into the steps it was produced in.
 *
 * The AI SDK marks the boundaries of a multi-step tool loop with `step-start`
 * parts, and emits text / reasoning / tool parts between them. Rendering that
 * array as one prose block is what produced the run-on paragraph — seven step
 * preambles flowing together with no break where one ended and the next began
 * (`…simultaneously now —Good — first wave is in.`).
 *
 * The previous fix inferred those breaks from punctuation, which cannot hold:
 * the boundary is as often an em dash or a colon as a full stop, and sometimes
 * there is no punctuation at all. The boundary is already stated explicitly one
 * layer up. Read it there.
 *
 * The final step is returned separately from the rest. Its text is the answer,
 * not a step label — the intermediate steps belong on a reasoning rail, the
 * answer belongs beneath it as prose.
 */

import { isUserVisibleTool } from '@/lib/ai/tool-visibility'
import { describeToolExecution } from '@/lib/ai/turn-activity'

/**
 * The SDK's step-boundary marker.
 *
 * Exported so that code REBUILDING a turn's parts — restoring one from the
 * database, say — can emit a boundary without spelling the literal itself.
 * `step-rail-wiring` asserts this module is the only place boundaries are
 * interpreted; handing out the marker keeps that true instead of tempting the
 * next caller to re-derive the split.
 */
export const STEP_START_PART_TYPE = 'step-start'

export interface MessageStep {
  /** 1-based, so the rail reads the same way the server logs do. */
  index: number
  /** What the model wrote during this step — usually its preamble. */
  text: string
  /** Tool names invoked in this step, in call order, duplicates kept. */
  tools: string[]
  /** Reasoning emitted during this step, joined if it arrived in pieces. */
  reasoning: string
}

export interface GroupedMessage {
  /** Every step except the last — the ones that belong on the rail. */
  steps: MessageStep[]
  /** The last step's text: the answer. */
  finalText: string
  /**
   * The last step whole — its reasoning and tools as well as its text.
   *
   * `finalText` keeps only the text, which is all the rail needs (the rail
   * never shows the last step). Anything PERSISTING a turn needs every step,
   * and re-splitting the parts to recover this one is the second
   * interpretation this module exists to prevent.
   */
  finalStep: MessageStep
  /**
   * Every step's text, in order, separated by a blank line.
   *
   * This is what the message body should render. The rail is additive — a
   * progress and navigation aid — and must not be the only place a step's
   * output exists. An earlier version rendered only `finalText` in the body,
   * which hid the intermediate steps' full markdown (tables included) behind
   * a 120-character rail label for the whole time a turn was streaming. The
   * run-on paragraph never required hiding anything: it came from joining the
   * parts with '' instead of a blank line.
   */
  allText: string
}

const EMPTY_STEP: MessageStep = { index: 0, text: '', tools: [], reasoning: '' }
const EMPTY: GroupedMessage = { steps: [], finalText: '', allText: '', finalStep: EMPTY_STEP }

interface RawPart {
  type?: string
  text?: string
  reasoning?: string
  content?: string
}

/** A step under construction, before we know whether it is the last one. */
interface Bucket {
  texts: string[]
  reasoning: string[]
  tools: string[]
}

const newBucket = (): Bucket => ({ texts: [], reasoning: [], tools: [] })

function textOf(part: RawPart): string {
  const raw = part.text ?? part.content ?? ''
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * Split `parts` on `step-start`, returning the intermediate steps and the
 * final step's text. A message with no boundaries is treated as a single
 * answer with no rail — absence of structure must not manufacture one.
 */
export function groupPartsIntoSteps(parts: unknown): GroupedMessage {
  if (!Array.isArray(parts)) return EMPTY

  const buckets: Bucket[] = []
  let current: Bucket | null = null

  for (const raw of parts) {
    if (!raw || typeof raw !== 'object') continue
    const part = raw as RawPart
    const type = part.type
    if (typeof type !== 'string') continue

    if (type === STEP_START_PART_TYPE) {
      current = newBucket()
      buckets.push(current)
      continue
    }

    // Only content opens a step. Anything else — the data-crucible-* status
    // and keepalive parts especially — is skipped before the bucket below is
    // created.
    //
    // It used to fall through, and because `data-crucible-tool-status` is the
    // FIRST part on the wire, every turn opened an empty bucket before its
    // first step-start. The trailing-empty cleanup below only pops from the
    // end, so that phantom never got collected: it survived as a blank rail
    // node (`{text:'', tools:[], reasoning:''}`) and pushed steps.length one
    // past the truth, so railHeading's `settledSteps + 1` announced "Working —
    // step 2" while the model was still on step 1. Measured on the rig
    // 2026-09-11: rail first painted at 3.90s already reading "step 2", with
    // the server log putting step 1's finish 9.34s after the request.
    if (
      type !== 'text' &&
      type !== 'reasoning' &&
      type !== 'thinking' &&
      !type.startsWith('tool-')
    ) {
      continue
    }

    // Content before the first boundary still belongs to a step.
    if (!current) {
      current = newBucket()
      buckets.push(current)
    }

    if (type === 'text') {
      const t = textOf(part)
      if (t) current.texts.push(t)
    } else if (type === 'reasoning' || type === 'thinking') {
      const t = part.text ?? part.reasoning ?? ''
      if (typeof t === 'string' && t.trim()) current.reasoning.push(t.trim())
    } else if (type.startsWith('tool-')) {
      current.tools.push(type.slice('tool-'.length))
    }
  }

  // A boundary arrives before its step produces anything, so a mid-stream
  // render sees a trailing empty bucket. Dropping it keeps an empty node off
  // the rail; a step that ran tools but wrote no text is NOT empty and stays.
  while (buckets.length && isEmpty(buckets[buckets.length - 1])) buckets.pop()
  if (!buckets.length) return EMPTY

  const last = buckets.pop() as Bucket
  const toStep = (b: Bucket, index: number): MessageStep => ({
    index,
    text: b.texts.join('\n\n'),
    tools: b.tools,
    reasoning: b.reasoning.join('\n\n'),
  })
  const steps = buckets.map((b, i) => toStep(b, i + 1))
  const finalStep = toStep(last, steps.length + 1)
  return {
    steps,
    finalText: finalStep.text,
    allText: [...steps.map((s) => s.text), finalStep.text].filter(Boolean).join('\n\n'),
    finalStep,
  }
}

function isEmpty(b: Bucket): boolean {
  return b.texts.length === 0 && b.reasoning.length === 0 && b.tools.length === 0
}

/** Longest a rail label may be before it stops being a label. */
export const STEP_LABEL_MAX = 120

/**
 * Heading for the rail.
 *
 * `steps` holds only the SETTLED steps — `groupPartsIntoSteps` pops the last
 * bucket into `finalText`, because a live turn's last bucket is the answer
 * being written. So while a turn streams, the step being worked on is the one
 * AFTER the last rail node, and naming `steps.length` announced the previous
 * one: observed live as "Working — step 2" on a turn whose server log recorded
 * steps 1, 2 and 3.
 *
 * Settled, the heading describes the nodes below it, so there it counts them.
 */
export function railHeading(settledSteps: number, isStreaming: boolean): string {
  if (isStreaming) return `Working — step ${settledSteps + 1}`
  return `${settledSteps} step${settledSteps === 1 ? '' : 's'}`
}

/**
 * What a step did, for a step that wrote no prose to label itself with.
 *
 * The rail already derives its ICON from `step.tools`; the label ignored them
 * and fell through to the string "Gathering". So a 142-second canvas write —
 * which emits no text at all — was announced as a lookup. Same data, and only
 * one of the two consumers was reading it.
 *
 * Returns the description of the LAST user-visible tool: the badges underneath
 * already enumerate every tool in the step, so the label's job is to say where
 * the step ended up. Null when there is nothing to describe, leaving the
 * caller's generic in place rather than inventing an activity.
 */
export function describeStepTools(tools: unknown): string | null {
  if (!Array.isArray(tools)) return null
  for (let i = tools.length - 1; i >= 0; i--) {
    const name: unknown = tools[i]
    if (typeof name === 'string' && isUserVisibleTool(name)) return describeToolExecution(name)
  }
  return null
}

/**
 * One short plain-text line for a rail node.
 *
 * A step's text is whatever the model wrote before its tool calls, and that is
 * frequently markdown — bold, a heading, sometimes an entire pipe table. The
 * rail's label slot is plain text, so passing it through renders literal `**`
 * and table pipes across the node (seen live: `✅**Step 3 complete —
 * …**  --- ## Step 3 — MAUDE: … | Report ID | Date | …`).
 *
 * Take the first meaningful line, strip the inline markers, and truncate. The
 * full text is not lost — it is the model's own progress narration, and the
 * answer it leads to renders in the message body.
 */
export function stepLabel(text: unknown, max: number = STEP_LABEL_MAX): string {
  if (typeof text !== 'string') return ''
  const firstLine =
    text
      .split('\n')
      // Skip rules, table rows, and heading-only lines — none of them read as
      // a label on their own.
      .map((l) => l.trim())
      .find((l) => l && !/^[-=*_|#>]+$/.test(l) && !l.startsWith('|')) ?? ''

  const plain = firstLine
    .replace(/^#{1,6}\s*/, '') // leading heading marker
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/(^|\s)\*(?!\s)(.+?)\*/g, '$1$2') // italic
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links
    .replace(/\s+/g, ' ')
    .trim()

  if (plain.length <= max) return plain
  // Prefer a word boundary so the ellipsis does not land mid-word.
  const cut = plain.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`
}
